// The Settings singleton tab's centre. Split out of Centre.tsx (hotspot
// decomposition); behaviour unchanged.

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { SettingsView } from "../../../components/AppShell/settingsView.js";
import { projectYourTexts, YourTexts } from "../../../modules/journal.js";
import { API_BASE } from "../../httpClient.js";
import { useAppShell } from "../context.js";
import { useJournal } from "../useJournal.js";

// Dev tools live as OMNIBAR COMMANDS, never in Settings (owner call), so
// Settings has no Advanced section.
// IAM props: real with a live cloud session — `authenticated` is the
// one `AuthStatus` arm that carries a `user`. `anonymous` never reaches
// here (AppShellHost's sign-in gate), so by the time Settings renders the
// author is always authenticated.
export const SettingsCentre = (): ReactNode => {
    const { t } = useLingui();
    const {
        frame,
        ports,
        authStatus,
        displayName,
        onDisplayNameChange,
        workspaceName,
        onWorkspaceNameChange,
        summaryConsent,
        onSummaryConsentChange,
        signOut,
    } = useAppShell();
    // The journal's own-text settings live here, not in the Journal; the
    // module is mounted for them (its page read carries the consent).
    const journal = useJournal(ports);
    const yourTexts = projectYourTexts(journal.model);
    return (
        <SettingsView
            initialSection={frame.settingsSection}
            identity={
                authStatus?._tag === "authenticated"
                    ? {
                          type: "authenticated",
                          email: authStatus.user.email,
                          displayName,
                      }
                    : { type: "pending", displayName }
            }
            onDisplayNameChange={onDisplayNameChange}
            // F56: the editable workspace name (raw draft) + the derived
            // display default as the field's placeholder, so an unnamed
            // workspace shows what its title will read as.
            workspaceName={workspaceName}
            workspaceNamePlaceholder={
                displayName ? t`${displayName}'s workspace` : t`Workspace`
            }
            onWorkspaceNameChange={onWorkspaceNameChange}
            summaryConsent={summaryConsent}
            onSummaryConsentChange={onSummaryConsentChange}
            journalTexts={
                yourTexts === null ? null : (
                    <YourTexts view={yourTexts} send={journal.send} />
                )
            }
            iamIdentity={
                authStatus?._tag === "authenticated"
                    ? {
                          userId: authStatus.user.id,
                          email: authStatus.user.email,
                      }
                    : undefined
            }
            iamPorts={authStatus?._tag === "authenticated" ? ports : undefined}
            onSignOut={signOut}
            // Admin — export the workspace event log. Reuses the SAME
            // `downloadExport` DOM trigger the package-export flow already
            // has (httpPorts/packaging.ts), just pointed at
            // `/account/export` instead of `/exports/:id` — a same-origin
            // attachment download the browser's session cookie authenticates,
            // so no new port is needed.
            onExportWorkspaceLog={
                authStatus?._tag === "authenticated"
                    ? () => ports.downloadExport(`${API_BASE}/account/export`)
                    : undefined
            }
            // FR-12.2: admins reach the account-administration page from
            // here; the server gates the page itself.
            adminAccountsHref={
                authStatus?._tag === "authenticated" && authStatus.user.isAdmin
                    ? `${API_BASE}/admin/users`
                    : undefined
            }
        />
    );
};
